// The front door: today's paper (five headlines, the same for everyone), yesterday's moves when
// the wire has any worth replaying, and how the player's instinct is doing. Nothing else.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "../components/Link";
import { Globe } from "../globe/Globe";
import type { GlobePoint } from "../globe/types";
import { dollars } from "../../shared/game/money.ts";
import { unlock } from "./fx";
import { useBoard } from "./board";
import { freshRounds } from "./fresh";
import { deckResult, instinctOf, MARGIN_CENTS, streakOf, useBestRun, usePlays } from "./record";
import { dailyDeck, dayNumber, DECK_SIZE, loadClassics } from "./rounds";
import "./game.css";

export const useClassics = () => useQuery({ queryKey: ["classics"], queryFn: loadClassics, staleTime: Number.POSITIVE_INFINITY });
export const useFresh = () => useQuery({ queryKey: ["fresh"], queryFn: () => freshRounds(), staleTime: 15 * 60_000, retry: 1 });

const today = () => new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

export function Home() {
  const day = dayNumber();
  const classics = useClassics();
  const fresh = useFresh();
  const all = usePlays();
  const deck = useMemo(() => (classics.data ? dailyDeck(classics.data, day) : []), [classics.data, day]);
  const result = deckResult(all, day);
  const finished = (result?.plays.length ?? 0) >= DECK_SIZE;
  const started = (result?.plays.length ?? 0) > 0;
  const streak = streakOf(all, day, DECK_SIZE);
  const instinct = instinctOf(all).slice(0, 4);
  const best = useBestRun();
  const players = useBoard(day, true).data?.of;

  const points: GlobePoint[] = useMemo(
    () => deck.filter((round) => round.place).map((round) => ({ id: round.id, lat: round.place?.lat ?? 0, lon: round.place?.lon ?? 0, weight: 0.45, tone: "warn" as const, label: round.place?.name ?? "" })),
    [deck],
  );

  return (
    <main className="hm">
      <div className="hm-globe">
        <Globe points={points} spin height="100%" />
      </div>
      <section className="hm-copy">
        <div className="hm-date num">
          {today()} · #{day}
        </div>
        <h1 className="hm-title">
          Five headlines.
          <br />
          Ten seconds each.
        </h1>
        <p className="hm-sub">Long or short. Then watch the next hour happen.</p>

        {finished && result ? (
          <Link href="/play" className="hm-cta is-done" onClick={unlock}>
            <span className="num">{dollars(result.bankroll)}</span> · see today's
          </Link>
        ) : (
          <Link href="/play" className="hm-cta" aria-disabled={!deck.length} onClick={unlock}>
            {started ? `Continue ${result?.plays.length}/${DECK_SIZE}` : "Play today's paper"}
          </Link>
        )}

        <Link href="/survival" className="hm-survival" onClick={unlock}>
          <span className="hm-survival-title">Survival</span>
          <span className="hm-survival-sub">Every headline in the archive, a tenth of your money each, until you're broke or walk away.</span>
          {best && <span className="hm-survival-best num">Best {dollars(BigInt(best.bankroll))}</span>}
        </Link>

        {fresh.data && fresh.data.length > 0 && (
          <Link href="/fresh" className="hm-fresh" onClick={unlock}>
            <span className="hm-fresh-label">Fresh</span>
            <span className="hm-fresh-title">{fresh.data.length === 1 ? fresh.data[0]?.headline : `${fresh.data.length} headlines from the last day`}</span>
            <span aria-hidden>→</span>
          </Link>
        )}
        {fresh.isLoading && <div className="hm-fresh is-loading">Reading the last day's wire…</div>}

        {players && players > 0 ? <p className="hm-players num">{players === 1 ? "1 player" : `${players} players`} on today's board</p> : null}
        <p className="hm-rules num">
          {dollars(MARGIN_CENTS)} a headline · up to 1000× · bust and fees as on papertrade
        </p>

        {(streak > 0 || instinct.length > 0) && (
          <div className="hm-stats num">
            {streak > 0 && <span>{streak}-day streak</span>}
            {instinct.map((entry) => (
              <span key={entry.category}>
                {entry.category} {entry.right}/{entry.taken}
              </span>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
